import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AutoComplete, AutoCompleteModule } from 'primeng/autocomplete';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { Product } from '../../../../modals/product.modals';
import { IndustryService } from '../../../../services/industry.service';
import { Customer } from '../../../../services/offer-group.service';
import {
  DoctorInfo,
  DoctorManagementDialogComponent,
} from '../../../industries/pharmacy/components/doctor-management-dialog/doctor-management-dialog.component';
import { UpiPaymentRequest } from '../../interfaces/payment-dialog.interface';
import { BillingPhase } from '../../models/network-health.model';
import { LoyaltyService } from '../../services/loyalty.service';
import { PaymentSessionService } from '../../services/payment-session.service';
/**
 * PaymentTotalDialogComponent
 *
 * Standalone presentational shell for the "Payment & Total Details" /
 * "Quotation Details" dialog. It owns ZERO business/financial logic — every
 * calculation, validation, payment-processing, loyalty, and exchange method
 * remains unchanged on DashboardComponent. This component only renders the
 * dialog markup and proxies user interaction back to the parent via
 * two-way bindings (raw field values) and @Output() actions (side-effecting
 * methods), exactly mirroring the bindings the inline template used to have.
 *
 * Replaces the inline <p-dialog> block at L1158–2277 of
 * dashboard.component.html.
 */
@Component({
  selector: 'app-payment-total-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputNumberModule,
    AutoCompleteModule,
    CalendarModule,
    CardModule,
    DoctorManagementDialogComponent,
  ],
  providers: [LoyaltyService, PaymentSessionService],
  templateUrl: './payment-total-dialog.component.html',
  styleUrl: './payment-total-dialog.component.scss',
})
export class PaymentTotalDialogComponent
  implements OnInit, AfterViewInit, OnChanges, OnDestroy
{
  constructor(
    public readonly loyalty: LoyaltyService,
    public readonly session: PaymentSessionService,
    private readonly messageService: MessageService,
    public readonly industryService: IndustryService,
  ) {}

  ngOnInit(): void {
    this.setupKeypadKeyboardListener();
  }

  // ── Dialog visibility / mode ────────────────────────────────────────────
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Input() isQuotationMode: boolean = false;
  @Input() isExchangeOrder: boolean = false;
  @Input() showExchangeConfirmation: boolean = false;

  // ── Billing guard phase (from BillingGuardService) ──────────────────────
  /** Drives the slow/critical network warning inside the payment dialog. */
  @Input() billingPhase: BillingPhase = BillingPhase.IDLE;
  /** Expose BillingPhase enum to the template */
  readonly BillingPhase = BillingPhase;

  // ── Customer info ───────────────────────────────────────────────────────
  @Input() mobileNumber: string = '';
  @Output() mobileNumberChange = new EventEmitter<string>();
  @Output() mobileNumberModelChange = new EventEmitter<any>();
  @Output() mobileNumberBlur = new EventEmitter<void>();
  @Input() customerName: string = '';
  @Output() customerNameChange = new EventEmitter<string>();
  @Output() customerNameModelChange = new EventEmitter<any>();
  @Output() customerNameBlur = new EventEmitter<void>();
  @Input() isMobileMandatory: boolean = false;
  @Input() filteredCustomersForMobile: Customer[] = [];
  @Input() filteredCustomers: Customer[] = [];
  @Output() filterCustomersByMobileRequested = new EventEmitter<any>();
  @Output() filterCustomersRequested = new EventEmitter<any>();
  @Output() mobileSelectFromAutocomplete = new EventEmitter<any>();
  @Output() customerSelectFromAutocomplete = new EventEmitter<any>();

  // ── Doctor info ──────────────────────────────────────────────────────────
  @Input() doctorName: string = '';
  @Output() doctorNameChange = new EventEmitter<string>();
  @Output() doctorNameModelChange = new EventEmitter<any>();
  @Input() doctorMobile: string = '';
  @Output() doctorMobileChange = new EventEmitter<string>();
  @Output() doctorMobileModelChange = new EventEmitter<any>();
  @Input() filteredDoctors: any[] = [];
  @Output() filterDoctorsRequested = new EventEmitter<any>();
  @Output() doctorSelectFromAutocomplete = new EventEmitter<any>();

  showDoctorDialog: boolean = false;
  currentDoctorData: Partial<DoctorInfo> | null = null;

  // Locked once per dialog open, from what the original sale already had —
  // never from live typing, so a field can't lock itself mid-edit.
  lockMobile = false;
  lockName = false;

  openDoctorDialog(): void {
    this.currentDoctorData = {
      doctorName: this.doctorName || '',
      doctorMobile: this.doctorMobile || '',
    };
    this.showDoctorDialog = true;
  }

  onDoctorSaved(info: DoctorInfo): void {
    this.doctorName = info.doctorName;
    if (info.doctorMobile) {
      this.doctorMobile = info.doctorMobile;
    }
    this.doctorNameChange.emit(this.doctorName);
    this.doctorNameModelChange.emit(this.doctorName);
    this.doctorMobileChange.emit(this.doctorMobile);
    this.doctorMobileModelChange.emit(this.doctorMobile);
  }

  onDoctorSelect(event: any): void {
    const doc = event;
    if (typeof doc === 'string') {
      this.onDoctorInfoChange(doc);
    } else if (doc) {
      this.doctorName = doc.doctor_name || doc.name || doc.doctorName || '';
      this.doctorMobile = doc.mobile_no || doc.mobile || doc.doctorMobile || '';
      this.doctorNameChange.emit(this.doctorName);
      this.doctorNameModelChange.emit(this.doctorName);
      this.doctorMobileChange.emit(this.doctorMobile);
      this.doctorMobileModelChange.emit(this.doctorMobile);
    }
    this.doctorSelectFromAutocomplete.emit(event);
  }

  // ── Exchange confirmation ───────────────────────────────────────────────
  @Input() exactTotal: number = 0;
  @Input() isAddToCreditSelected: boolean = false;
  @Output() confirmExchangeAsCreditClicked = new EventEmitter<void>();
  @Output() confirmExchangeAsRefundClicked = new EventEmitter<void>();
  @Output() cancelExchangeConfirmationClicked = new EventEmitter<void>();

  // ── Payment tabs ─────────────────────────────────────────────────────────
  // REFACTORED: activePaymentTab, cashAmount, cardDetails, upiDetails,
  // splitPaymentAmounts, isPartPaymentCash/Card/UPI, redeemCreditAmount,
  // refundCashAmount, availableCreditAmount, selectedInvoiceNo now live on
  // the injected PaymentSessionService (see constructor) — template binds
  // to `session.state.*` directly instead of a separate @Input() per field.
  // The action/trigger @Output()s below are kept: setActivePaymentTab,
  // onPartPaymentToggle, and the validate*Amount methods they trigger still
  // orchestrate cross-cutting DashboardComponent state (scanner mode,
  // credit-fetch API calls, balance) and remain on DashboardComponent.
  @Output() setActivePaymentTab = new EventEmitter<
    'Cash' | 'Card' | 'UPI' | 'Redeem Points' | 'Split'
  >();
  @Input() showRedeemTab: boolean = false;
  @Input() isAnyPartPaymentEnabled: boolean = false;
  @Input() hasNonGSTProducts: boolean = false;

  // ── Cash tab ─────────────────────────────────────────────────────────────
  @Output() cashAmountInput = new EventEmitter<void>();
  @Output() partPaymentCashToggle = new EventEmitter<boolean>();
  @Input() absoluteBalance: number = 0;

  // ── Card tab ─────────────────────────────────────────────────────────────
  @Output() cardAmountInput = new EventEmitter<void>();
  @Output() partPaymentCardToggle = new EventEmitter<boolean>();

  // ── UPI tab ──────────────────────────────────────────────────────────────
  @Output() upiAmountInput = new EventEmitter<void>();
  @Output() partPaymentUPIToggle = new EventEmitter<boolean>();
  @Output() upiPaymentRequest = new EventEmitter<UpiPaymentRequest>();
  @Input() upiQrCodeImage: string | null = null;
  @Input() upiQrExpirySeconds: number | null = null;

  // ── Redeem Points / Add to Credit tab ───────────────────────────────────
  @Input() roundedTotal: number = 0;
  @Output() invoiceNumberInput = new EventEmitter<void>();
  @Output() redeemCreditAmountInput = new EventEmitter<void>();
  /** Emitted when the inline Redeem toggle (inside Cash/Card tab) is switched.
   *  true  = user turned Redeem ON  → parent should add 'Redeem Credit' to selectedPaymentOptions
   *  false = user turned Redeem OFF → parent should remove 'Redeem Credit' and reset amounts */
  @Output() redeemToggled = new EventEmitter<boolean>();
  showRedeemCodeInput: boolean = false;

  // ── Split tab ────────────────────────────────────────────────────────────
  @Output() splitAmountInput = new EventEmitter<void>();

  // ── Payment summary ──────────────────────────────────────────────────────
  @Input() paidAmount: number = 0;
  @Input() tipAmount: number = 0;
  @Input() balanceAmount: number = 0;
  @Input() balanceLabel: string = '';
  @Input() isBalanceNegative: boolean = false;

  // ── Loyalty ──────────────────────────────────────────────────────────────
  // REFACTORED: loyalty state/logic now owned by the injected LoyaltyService
  // (see constructor) — the template calls `loyalty.*` directly instead of
  // going through a separate @Input()/@Output() per field.

  // ── Quotation mode ───────────────────────────────────────────────────────
  @Input() quotationValidityDate: Date = new Date();
  @Output() quotationValidityDateChange = new EventEmitter<Date>();
  @Input() minValidityDate: Date = new Date();
  @Input() maxValidityDate: Date = new Date();
  @Input() products: Product[] = [];
  @Input() isWholesaleMode: boolean = false;
  @Input() isProcessingQuotation: boolean = false;
  @Input() canCompleteQuotation: boolean = false;
  @Output() processQuotationClicked = new EventEmitter<void>();

  // ── Footer / process payment ────────────────────────────────────────────
  @Input() canCompletePayment: boolean = false;
  @Input() exactTotalRoundOff: number = 0; // getRoundOffAmount()
  @Input() currentPartPaymentAmount: number = 0;
  @Input() remainingCreditAmount: number = 0;
  @Output() processPaymentClicked = new EventEmitter<void>();
  @Output() closeTotalDialogClicked = new EventEmitter<void>();

  // ── Numeric keypad ───────────────────────────────────────────────────────
  // REFACTORED: keypad handling moved in verbatim from DashboardComponent —
  // it manipulates document.activeElement scoped to .payment-input-section/
  // .split-payment-section (this component's own template) and mutates the
  // now-here-owned session amounts. See methods below constructor/ngOnInit.

  // ── Dialog lifecycle ─────────────────────────────────────────────────────
  @Output() dialogShow = new EventEmitter<void>();
  @Output() dialogKeyDown = new EventEmitter<KeyboardEvent>();
  @Output() upiQrExpired = new EventEmitter<void>();

  isPgIntegrationEnabled: boolean = false;
  private userInfo: { pgintegration?: number | string } = {};
  qrCountdownSeconds: number = 0;
  private qrCountdownTimer: any;

  // ── ViewChild references (dialog-local DOM behavior only) ───────────────
  @ViewChild('customerNameAutocomplete')
  customerNameAutocomplete!: AutoComplete;
  @ViewChild('mobileNumberAutocomplete')
  mobileNumberAutocomplete!: AutoComplete;
  @ViewChild('doctorNameAutocomplete') doctorNameAutocomplete!: AutoComplete;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue) {
      this.syncUserInfoFromStorage();
      // Snapshot which customer fields the original sale already had.
      this.lockMobile =
        this.isExchangeOrder &&
        /^\d{10}$/.test((this.mobileNumber || '').trim());
      this.lockName =
        this.isExchangeOrder && this.isRealValue(this.customerName);

      if (this.session.state.activePaymentTab === 'UPI') {
        this.handleIntegratedUpiActivation();
      }
    }

    if (changes['visible']?.currentValue === false) {
      this.isPgIntegrationEnabled = false;
      this.showRedeemCodeInput = false;
      this.stopQrCountdown();
    }

    if (changes['upiQrCodeImage']) {
      if (changes['upiQrCodeImage'].currentValue) {
        this.startQrCountdown(this.upiQrExpirySeconds);
      } else {
        this.stopQrCountdown();
      }
    }

    if (changes['upiQrExpirySeconds'] && this.upiQrCodeImage) {
      this.startQrCountdown(this.upiQrExpirySeconds);
    }
  }

  ngOnDestroy(): void {
    this.stopQrCountdown();
  }

  ngAfterViewInit(): void {
    // Customer Name Autocomplete: prevent Tab from auto-selecting the first
    // item (customer names are NOT unique), and route blur to the parent's
    // debounce-clear handler. Moved verbatim from DashboardComponent's
    // ngAfterViewInit — pure keyboard/focus UI behavior, no business logic.
    if (this.customerNameAutocomplete && this.customerNameAutocomplete.el) {
      const inputElement =
        this.customerNameAutocomplete.el.nativeElement.querySelector('input');
      if (inputElement) {
        inputElement.addEventListener('blur', () => {
          this.customerNameBlur.emit();
        });

        inputElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (
              event.key === 'Tab' &&
              this.customerNameAutocomplete.overlayVisible
            ) {
              event.preventDefault();
              event.stopPropagation();
              this.customerNameAutocomplete.hide();

              const nextElement =
                inputElement.parentElement?.parentElement?.nextElementSibling?.querySelector(
                  'input',
                );
              if (nextElement) {
                setTimeout(() => (nextElement as HTMLElement).focus(), 0);
              }
            }
          },
          true,
        );
      }
    }

    if (this.mobileNumberAutocomplete && this.mobileNumberAutocomplete.el) {
      const inputElement =
        this.mobileNumberAutocomplete.el.nativeElement.querySelector('input');
      if (inputElement) {
        inputElement.setAttribute('maxlength', '10');
        inputElement.setAttribute('inputmode', 'numeric');
        inputElement.setAttribute('pattern', '[0-9]*');

        inputElement.addEventListener('keypress', (event: KeyboardEvent) => {
          const charCode = event.which ? event.which : event.keyCode;
          const isMainKeyboardNumber = charCode >= 48 && charCode <= 57;
          const isNumpadNumber = charCode >= 96 && charCode <= 105;
          const isSpecialKey = [8, 9, 13, 46, 37, 38, 39, 40].includes(
            charCode,
          );

          if (!isMainKeyboardNumber && !isNumpadNumber && !isSpecialKey) {
            event.preventDefault();
          }
        });

        inputElement.addEventListener('paste', (event: ClipboardEvent) => {
          event.preventDefault();
          const pastedText = event.clipboardData?.getData('text') || '';
          const numericValue = pastedText
            .replace(/[^0-9]/g, '')
            .substring(0, 10);

          this.mobileNumber = numericValue;
          this.mobileNumberChange.emit(numericValue);
          const target = event.target as HTMLInputElement;
          if (target) {
            target.value = numericValue;
          }
        });

        inputElement.addEventListener('input', (event: any) => {
          const numericValue = event.target.value.replace(/[^0-9]/g, '');
          if (event.target.value !== numericValue) {
            event.target.value = numericValue;
          }
        });

        inputElement.addEventListener('blur', () => {
          this.mobileNumberBlur.emit();
        });

        inputElement.addEventListener(
          'keydown',
          (event: KeyboardEvent) => {
            if (
              event.key === 'Tab' &&
              this.mobileNumberAutocomplete.overlayVisible
            ) {
              event.preventDefault();
              event.stopPropagation();
              this.mobileNumberAutocomplete.hide();

              const nextElement =
                inputElement.parentElement?.parentElement?.nextElementSibling?.querySelector(
                  'input',
                );
              if (nextElement) {
                setTimeout(() => (nextElement as HTMLElement).focus(), 0);
              }
            }
          },
          true,
        );
      }
    }
  }

  // ── Glue handlers (value sync + side-effect trigger, mirrors the
  //    original inline [(ngModel)] + (onInput)/(ngModelChange) pairing) ────

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
  }

  onMobileNumberChange(value: string): void {
    this.mobileNumber = value;
    this.mobileNumberChange.emit(value);
  }

  onMobileNumberSelect(event: any): void {
    this.mobileSelectFromAutocomplete.emit(event);
  }

  onCustomerNameChange(value: string): void {
    this.customerName = value;
    this.customerNameChange.emit(value);
  }

  onCustomerNameSelect(event: any): void {
    this.customerSelectFromAutocomplete.emit(event);
  }

  onDoctorNameChange(value: string): void {
    this.doctorName = value;
    this.doctorNameChange.emit(value);
  }

  private isRealValue(v: string | null | undefined): boolean {
    const s = (v ?? '').toString().trim().toLowerCase();
    return (
      !!s &&
      ![
        'null',
        'undefined',
        '0',
        'walk in customer',
        'walk-in customer',
      ].includes(s)
    );
  }

  onDoctorMobileChange(value: string): void {
    const numeric = value ? value.replace(/[^0-9]/g, '').substring(0, 10) : '';
    this.doctorMobile = numeric;
    this.doctorMobileChange.emit(numeric);
  }

  resetRedeemState(): void {
    if (!this.showRedeemCodeInput) {
      return;
    }
    this.showRedeemCodeInput = false;
    if (this.session?.state) {
      if (!this.isExchangeOrder) {
        this.session.state.selectedInvoiceNo = '';
      }
      this.session.state.redeemCreditAmount = 0;
      this.session.state.availableCreditAmount = 0;
      this.redeemCreditAmountInput.emit();
    }
    // Notifies parent (Dashboard) to remove 'Redeem Credit' from selectedPaymentOptions
    this.redeemToggled.emit(false);
  }

  onDoctorInfoChange(value: string): void {
    if (!value) {
      this.doctorName = '';
      this.doctorMobile = '';
      this.doctorNameChange.emit('');
      this.doctorMobileChange.emit('');
      return;
    }

    if (value.includes(' - ')) {
      const parts = value.split(' - ');
      this.doctorName = parts[0].trim();
      this.doctorMobile = parts[1].trim();
    } else {
      const numeric = value.replace(/[^0-9]/g, '');
      if (numeric.length === 10 && numeric === value.trim()) {
        this.doctorMobile = numeric;
      } else {
        this.doctorName = value;
      }
    }
    this.doctorNameChange.emit(this.doctorName);
    this.doctorMobileChange.emit(this.doctorMobile);
  }

  onQuotationValidityDateChange(value: Date): void {
    this.quotationValidityDate = value;
    this.quotationValidityDateChange.emit(value);
  }

  onPartPaymentCashChange(value: boolean): void {
    if (this.session && this.session.state) {
      this.session.state.isPartPaymentCash = value;
      if (value) {
        this.session.state.isPartPaymentCard = false;
        this.session.state.isPartPaymentUPI = false;
        this.showRedeemCodeInput = false;
        this.resetRedeemState();
      }
    }
    this.partPaymentCashToggle.emit(value);
  }

  onPartPaymentCardChange(value: boolean): void {
    if (this.session && this.session.state) {
      this.session.state.isPartPaymentCard = value;
      if (value) {
        this.session.state.isPartPaymentCash = false;
        this.session.state.isPartPaymentUPI = false;
        this.showRedeemCodeInput = false;
        this.resetRedeemState();
      }
    }
    this.partPaymentCardToggle.emit(value);
  }

  onPartPaymentUPIChange(value: boolean): void {
    if (this.session && this.session.state) {
      this.session.state.isPartPaymentUPI = value;
      if (value) {
        this.session.state.isPartPaymentCash = false;
        this.session.state.isPartPaymentCard = false;
        this.showRedeemCodeInput = false;
        this.resetRedeemState();
      }
    }
    this.partPaymentUPIToggle.emit(value);
  }

  toggleRedeemCode(): void {
    if (this.isExchangeOrder) {
      this.onSelectPaymentTab('Redeem Points');
      return;
    }
    this.showRedeemCodeInput = !this.showRedeemCodeInput;
    if (!this.showRedeemCodeInput && this.session?.state) {
      this.session.state.selectedInvoiceNo = '';
      this.session.state.redeemCreditAmount = 0;
      this.session.state.availableCreditAmount = 0;
      this.redeemCreditAmountInput.emit();
    }
    // Notify parent so it can add/remove 'Redeem Credit' from selectedPaymentOptions
    this.redeemToggled.emit(this.showRedeemCodeInput);
  }

  onSelectPaymentTab(
    tab: 'Cash' | 'Card' | 'UPI' | 'Redeem Points' | 'Split',
  ): void {
    if (this.session?.state?.activePaymentTab !== tab) {
      this.resetRedeemState();
    }
    if (tab === 'UPI') {
      this.syncUserInfoFromStorage();
      this.handleIntegratedUpiActivation();
    }
    this.setActivePaymentTab.emit(tab);
  }

  private syncUserInfoFromStorage(): void {
    try {
      this.userInfo = JSON.parse(localStorage.getItem('name') || '{}');
    } catch {
      this.userInfo = {};
    }

    this.isPgIntegrationEnabled = Number(this.userInfo?.pgintegration) === 1;
  }

  private handleIntegratedUpiActivation(): void {
    if (!this.isPgIntegrationEnabled) {
      return;
    }

    this.session.state.upiDetails.upiAmount = this.roundedTotal;

    if (!this.isValidUpiCustomerInfo()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'UPI Validation',
        detail: 'Customer name and mobile number are required for UPI payment.',
        life: 3000,
      });
      this.stopQrCountdown();
      this.upiQrExpired.emit();
      return;
    }

    this.session.state.upiDetails.upiAmount = this.roundedTotal;
    this.session.state.upiDetails.upiId = null;
    this.session.state.upiDetails.transactionId = '';

    this.upiPaymentRequest.emit({
      customerName: (this.customerName || '').trim(),
      mobileNumber: (this.mobileNumber || '').trim(),
      amount: this.roundedTotal,
      paymentMethod: 'UPI',
      paymentChannel: 'UPI_INTENT',
    });
  }

  private isValidUpiCustomerInfo(): boolean {
    const customerName = (this.customerName || '').trim();
    const mobileNumber = (this.mobileNumber || '').trim();

    return !!customerName && mobileNumber.length === 10;
  }

  private startQrCountdown(initialSeconds: number | null = null): void {
    const DEFAULT_QR_COUNTDOWN_SECONDS = 150;
    this.stopQrCountdown();
    this.qrCountdownSeconds =
      Number(initialSeconds) > 0
        ? Number(initialSeconds)
        : DEFAULT_QR_COUNTDOWN_SECONDS;

    this.qrCountdownTimer = setInterval(() => {
      this.qrCountdownSeconds = Math.max(this.qrCountdownSeconds - 1, 0);

      if (this.qrCountdownSeconds <= 0) {
        this.stopQrCountdown();
        this.upiQrExpired.emit();
      }
    }, 1000);
  }

  private stopQrCountdown(): void {
    if (this.qrCountdownTimer) {
      clearInterval(this.qrCountdownTimer);
      this.qrCountdownTimer = null;
    }
    this.qrCountdownSeconds = 0;
  }

  get qrCountdownLabel(): string {
    const minutes = Math.floor(this.qrCountdownSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = (this.qrCountdownSeconds % 60).toString().padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  // ── Numeric keypad (moved verbatim from DashboardComponent) ─────────────

  onKeypadInput(digit: string): void {
    const focusedElement = document.activeElement as HTMLInputElement;

    if (this.isMobileNumberFocused(focusedElement)) {
      if (this.lockMobile) return;
      if (digit === '.') {
        return;
      }
      const currentValue = this.mobileNumber || '';
      const start = focusedElement.selectionStart ?? currentValue.length;
      const end = focusedElement.selectionEnd ?? currentValue.length;
      const newValue = (
        currentValue.substring(0, start) +
        digit +
        currentValue.substring(end)
      )
        .replace(/[^0-9]/g, '')
        .substring(0, 10);

      this.onMobileNumberChange(newValue);
      focusedElement.value = newValue;
      const newCursorPos = Math.min(start + digit.length, newValue.length);
      try {
        focusedElement.setSelectionRange(newCursorPos, newCursorPos);
      } catch (e) {}
      return;
    }

    const isPaymentAmountInput =
      focusedElement &&
      focusedElement.tagName === 'INPUT' &&
      (focusedElement.closest('.payment-input-section') ||
        focusedElement.closest('.split-payment-section')) &&
      !focusedElement.closest('.customer-info-section');

    if (isPaymentAmountInput) {
      const currentValue = focusedElement.value || '';
      const numericValue =
        parseFloat(currentValue.replace(/[^\d.-]/g, '')) || 0;

      if (numericValue === 0 && digit !== '.') {
        focusedElement.value = '';
        focusedElement.dispatchEvent(new Event('input', { bubbles: true }));

        setTimeout(() => {
          focusedElement.value = digit;
          focusedElement.dispatchEvent(new Event('input', { bubbles: true }));
        }, 10);
      } else {
        // COMMENTED OUT: Synthetic KeyboardEvent is blocked by browsers for inserting text
        /*
        const keyEvent = new KeyboardEvent('keydown', {
          key: digit,
          code: `Digit${digit}`,
          keyCode: digit.charCodeAt(0),
          which: digit.charCodeAt(0),
          bubbles: true,
          cancelable: true,
        });
        focusedElement.dispatchEvent(keyEvent);
        */

        if (digit === '.' && currentValue.includes('.')) {
          return;
        }

        const start = focusedElement.selectionStart ?? currentValue.length;
        const end = focusedElement.selectionEnd ?? currentValue.length;
        const newValue =
          currentValue.substring(0, start) +
          digit +
          currentValue.substring(end);
        focusedElement.value = newValue;
        const newCursorPos = start + digit.length;
        try {
          focusedElement.setSelectionRange(newCursorPos, newCursorPos);
        } catch (e) {}

        focusedElement.dispatchEvent(new Event('input', { bubbles: true }));
        focusedElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      this.addToActivePaymentAmount(digit);
    }
  }

  onKeypadBackspace(): void {
    const focusedElement = document.activeElement as HTMLInputElement;

    if (this.isMobileNumberFocused(focusedElement)) {
      if (this.lockMobile) return;
      const currentValue = this.mobileNumber || '';
      const start = focusedElement.selectionStart ?? currentValue.length;
      const end = focusedElement.selectionEnd ?? currentValue.length;

      let newValue = currentValue;
      let newCursorPos = start;

      if (start !== end) {
        newValue =
          currentValue.substring(0, start) + currentValue.substring(end);
      } else if (start > 0) {
        newValue =
          currentValue.substring(0, start - 1) + currentValue.substring(start);
        newCursorPos = start - 1;
      }

      this.onMobileNumberChange(newValue);
      focusedElement.value = newValue;
      try {
        focusedElement.setSelectionRange(newCursorPos, newCursorPos);
      } catch (e) {}
      return;
    }

    // Check if focused element is a payment amount input (not mobile number or other inputs)
    const isPaymentAmountInput =
      focusedElement &&
      focusedElement.tagName === 'INPUT' &&
      (focusedElement.closest('.payment-input-section') ||
        focusedElement.closest('.split-payment-section')) &&
      !focusedElement.closest('.customer-info-section');

    if (isPaymentAmountInput) {
      // COMMENTED OUT: Synthetic KeyboardEvent is blocked by browsers for deleting text
      /*
      // For payment amount inputs, let PrimeNG handle the backspace naturally
      // Just simulate a backspace keypress event
      const keyEvent = new KeyboardEvent('keydown', {
        key: 'Backspace',
        code: 'Backspace',
        keyCode: 8,
        which: 8,
        bubbles: true,
        cancelable: true,
      });
      focusedElement.dispatchEvent(keyEvent);
      */

      const val = focusedElement.value || '';
      const start = focusedElement.selectionStart ?? val.length;
      const end = focusedElement.selectionEnd ?? val.length;

      let newValue = val;
      let newCursorPos = start;

      if (start !== end) {
        newValue = val.substring(0, start) + val.substring(end);
      } else if (start > 0) {
        newValue = val.substring(0, start - 1) + val.substring(start);
        newCursorPos = start - 1;
      }

      focusedElement.value = newValue;
      try {
        focusedElement.setSelectionRange(newCursorPos, newCursorPos);
      } catch (e) {}

      focusedElement.dispatchEvent(new Event('input', { bubbles: true }));
      focusedElement.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      // For non-focused inputs, use our custom logic
      this.backspaceActivePaymentAmount();
    }
  }

  onKeypadClear(): void {
    const focusedElement = document.activeElement as HTMLInputElement;

    if (this.isMobileNumberFocused(focusedElement)) {
      if (this.lockMobile) return;
      this.onMobileNumberChange('');
      focusedElement.value = '';
      return;
    }

    if (focusedElement && focusedElement.tagName === 'INPUT') {
      focusedElement.value = '';
      focusedElement.dispatchEvent(new Event('input', { bubbles: true }));
    } else {
      this.clearActivePaymentAmount();
    }
  }

  // Returns true when the given element is the mobile number field's native
  // <input> — used so the on-screen numeric keypad types into the mobile
  // number field directly instead of falling through to the active payment
  // tab's amount (that field lives inside .customer-info-section, which is
  // intentionally excluded from the payment-amount-input detection below).
  private isMobileNumberFocused(element: HTMLInputElement): boolean {
    const mobileInputEl =
      this.mobileNumberAutocomplete?.el?.nativeElement?.querySelector('input');
    return !!mobileInputEl && element === mobileInputEl;
  }

  preventFocus(event: MouseEvent): void {
    event.preventDefault();
    (event.target as HTMLElement).blur();
  }

  // Setup keyboard listener for keypad animation — set up once for this
  // component's lifetime (never destroyed, since it's not *ngIf-gated in
  // the parent template), matching the original's "set up once" semantics.
  private setupKeypadKeyboardListener(): void {
    document.addEventListener('keydown', (event) => {
      // Only trigger in payment dialog
      if (!this.visible) return;

      const key = event.key;
      let keypadButton: HTMLElement | null = null;
      let shouldPreventDefault = false;

      const focusedElement = document.activeElement as HTMLInputElement;
      // Check if focused element is ANY input field
      const isFocusedOnAnyInput =
        focusedElement && focusedElement.tagName === 'INPUT';

      // Check if focused element is a payment amount input specifically
      const isPaymentAmountInput =
        focusedElement &&
        focusedElement.tagName === 'INPUT' &&
        (focusedElement.closest('.payment-input-section') ||
          focusedElement.closest('.split-payment-section')) &&
        !focusedElement.closest('.customer-info-section');

      // Map keyboard keys to keypad buttons
      if (key >= '0' && key <= '9') {
        keypadButton = document.querySelector(`[data-keypad="${key}"]`);
        // Don't prevent default if ANY input is focused (let normal input behavior work)
        if (!isFocusedOnAnyInput) {
          shouldPreventDefault = true;
        }
      } else if (key === '.' || key === ',') {
        keypadButton = document.querySelector('[data-keypad="."]');
        if (!isFocusedOnAnyInput) {
          shouldPreventDefault = true;
        }
      } else if (key === 'Backspace') {
        keypadButton = document.querySelector('[data-keypad="backspace"]');
        if (!isFocusedOnAnyInput) {
          shouldPreventDefault = true;
        }
      } else if (key === 'Delete' || key === 'Escape') {
        keypadButton = document.querySelector('[data-keypad="clear"]');
        if (!isFocusedOnAnyInput) {
          shouldPreventDefault = true;
        }
      }

      if (keypadButton && shouldPreventDefault) {
        event.preventDefault();
        event.stopPropagation();
      }

      if (keypadButton) {
        // Add pressed animation
        keypadButton.classList.add('keypad-pressed');

        // Only trigger keypad functions if no input is focused
        if (!isFocusedOnAnyInput) {
          if (key >= '0' && key <= '9') {
            this.onKeypadInput(key);
          } else if (key === '.' || key === ',') {
            this.onKeypadInput('.');
          } else if (key === 'Backspace') {
            this.onKeypadBackspace();
          } else if (key === 'Delete' || key === 'Escape') {
            this.onKeypadClear();
          }
        }

        setTimeout(() => {
          keypadButton?.classList.remove('keypad-pressed');
        }, 150);
      }
    });
  }

  // ── One-tap tender amounts ─────────────────────────────────────────────
  // Exact amount plus the next round notes, so most bills need one tap instead of typing.

  /** Cash, Card and UPI take a single amount; Split, credit and returns keep manual entry */
  get showQuickTender(): boolean {
    const tab = this.session.state.activePaymentTab;
    return (
      !this.isExchangeOrder &&
      !this.isAnyPartPaymentEnabled &&
      this.roundedTotal > 0 &&
      (tab === 'Cash' || tab === 'Card' || tab === 'UPI')
    );
  }

  /** Exact due, then the next multiples of 100 / 500 / 2000 above it (cash only), at most four */
  get quickTenderOptions(): number[] {
    const due = this.roundedTotal;
    if (this.session.state.activePaymentTab !== 'Cash') return [due];
    const options = [due];
    for (const step of [100, 500, 2000]) {
      const next = Math.ceil(due / step) * step;
      if (next > due && !options.includes(next)) options.push(next);
    }
    return options.slice(0, 4);
  }

  /** Same path as typing into the active tender's amount box: set the value, then emit its input event */
  setTenderAmount(amount: number): void {
    const state = this.session.state;
    if (state.activePaymentTab === 'Cash') {
      state.cashAmount = amount;
      this.cashAmountInput.emit();
    } else if (state.activePaymentTab === 'Card') {
      state.cardDetails.amount = amount;
      this.cardAmountInput.emit();
    } else if (state.activePaymentTab === 'UPI') {
      state.upiDetails.upiAmount = amount;
      this.upiAmountInput.emit();
    }
  }

  isTenderSelected(amount: number): boolean {
    const state = this.session.state;
    const current =
      state.activePaymentTab === 'Cash'
        ? state.cashAmount
        : state.activePaymentTab === 'Card'
          ? state.cardDetails.amount
          : state.upiDetails.upiAmount;
    return Number(current) === amount;
  }

  private addToActivePaymentAmount(digit: string): void {
    const state = this.session.state;
    if (state.activePaymentTab === 'Split') {
      const currentValue = (state.splitPaymentAmounts.cash || 0).toString();
      state.splitPaymentAmounts.cash = parseFloat(currentValue + digit) || 0;
    } else if (state.activePaymentTab === 'Cash') {
      const currentValue = (state.cashAmount || 0).toString();
      state.cashAmount = parseFloat(currentValue + digit) || 0;
    } else if (state.activePaymentTab === 'Card') {
      const currentValue = (state.cardDetails.amount || 0).toString();
      state.cardDetails.amount = parseFloat(currentValue + digit) || 0;
    } else if (state.activePaymentTab === 'UPI') {
      const currentValue = (state.upiDetails.upiAmount || 0).toString();
      state.upiDetails.upiAmount = parseFloat(currentValue + digit) || 0;
    } else if (state.activePaymentTab === 'Redeem Points') {
      const currentValue = (state.redeemCreditAmount || 0).toString();
      state.redeemCreditAmount = parseFloat(currentValue + digit) || 0;
      this.redeemCreditAmountInput.emit();
    }
  }

  private backspaceActivePaymentAmount(): void {
    const state = this.session.state;
    if (state.activePaymentTab === 'Split') {
      const currentValue = (state.splitPaymentAmounts.cash || 0).toString();
      state.splitPaymentAmounts.cash =
        parseFloat(currentValue.slice(0, -1)) || 0;
    } else if (state.activePaymentTab === 'Cash') {
      const currentValue = (state.cashAmount || 0).toString();
      state.cashAmount = parseFloat(currentValue.slice(0, -1)) || 0;
    } else if (state.activePaymentTab === 'Card') {
      const currentValue = (state.cardDetails.amount || 0).toString();
      state.cardDetails.amount = parseFloat(currentValue.slice(0, -1)) || 0;
    } else if (state.activePaymentTab === 'UPI') {
      const currentValue = (state.upiDetails.upiAmount || 0).toString();
      state.upiDetails.upiAmount = parseFloat(currentValue.slice(0, -1)) || 0;
    } else if (state.activePaymentTab === 'Redeem Points') {
      const currentValue = (state.redeemCreditAmount || 0).toString();
      state.redeemCreditAmount = parseFloat(currentValue.slice(0, -1)) || 0;
      this.redeemCreditAmountInput.emit();
    }
  }

  private clearActivePaymentAmount(): void {
    const state = this.session.state;
    if (state.activePaymentTab === 'Split') {
      state.splitPaymentAmounts = { cash: 0, card: 0, upi: 0 };
    } else if (state.activePaymentTab === 'Cash') {
      state.cashAmount = 0;
    } else if (state.activePaymentTab === 'Card') {
      state.cardDetails.amount = 0;
    } else if (state.activePaymentTab === 'UPI') {
      state.upiDetails.upiAmount = 0;
    } else if (state.activePaymentTab === 'Redeem Points') {
      state.redeemCreditAmount = 0;
    }
  }
}
